import type { Location } from '@/types/location';
import { Autocomplete, Box, Chip, CircularProgress, TextField, Typography } from '@mui/material';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect, useMemo, useState } from 'react';
import {
  type Control,
  Controller,
  type FieldErrors,
  type UseFormSetValue,
  type UseFormWatch,
} from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { useGetLocationsQuery } from '@/store/api/services/locations/locations';

interface LocationSelectProps {
  control: Control<any>;
  errors: FieldErrors<any>;
  disabled?: boolean;
  label?: string;
  helperText?: string;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  watchedCaseId?: string | undefined;
}

export const LocationSelect = ({
  control,
  errors,
  disabled = false,
  label = 'Location *',
  helperText,
  setValue,
  watch,
  watchedCaseId,
}: LocationSelectProps) => {
  const [open, setOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [allLocations, setAllLocations] = useState<Location[]>([]);
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const [searchParams] = useSearchParams();
  const locationIdFromUrl = searchParams.get('location_id');
  const watchedLocationId = watch('location_id');

  // Throttle/debounce search text (500ms delay)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchText(inputValue);
    }, 500);

    return () => clearTimeout(timer);
  }, [inputValue]);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchText]);

  // Fetch locations with search (like case create)
  const shouldFetch =
    !disabled && (open || !!locationIdFromUrl || (!!watchedLocationId && !selectedLocation));
  const querySearchText = open
    ? debouncedSearchText || undefined
    : watchedLocationId || locationIdFromUrl || undefined;

  const { data, isLoading, isFetching } = useGetLocationsQuery(
    shouldFetch
      ? {
          search_text: querySearchText || undefined,
          pagination: {
            current_page: currentPage,
            items_per_page: 100,
            sort_ascending: true,
            sort_by: 'location_name',
          },
        }
      : skipToken,
    {
      refetchOnMountOrArgChange: true,
    },
  );

  // Auto-load all pages
  useEffect(() => {
    if (data?.data) {
      const responseSearchText = String((data.meta as any)?.search_text || '').trim();
      const currentSearch = String(debouncedSearchText || '').trim();

      if (open && responseSearchText !== currentSearch) {
        return;
      }

      if (currentPage === 1) {
        const newLocations = data.data;
        if (
          selectedLocation &&
          !newLocations.find((l) => l.location_id === selectedLocation.location_id)
        ) {
          setAllLocations([selectedLocation, ...newLocations]);
        } else {
          setAllLocations(newLocations);
        }
      } else {
        // Subsequent pages - append
        setAllLocations((prev) => {
          // Avoid duplicates
          const existingIds = new Set(prev.map((l) => l.location_id));
          const newLocations = data.data.filter((l) => !existingIds.has(l.location_id));
          return [...prev, ...newLocations];
        });
      }
    }
  }, [data, currentPage, selectedLocation, debouncedSearchText, open]);

  const totalPages = data?.meta?.pagination?.total_pages || 1;
  const hasMore = currentPage < totalPages;

  const options = useMemo(() => {
    return allLocations;
  }, [allLocations]);

  // Pre-select location from URL params once data is loaded
  useEffect(() => {
    if (locationIdFromUrl && options.length > 0 && control && !disabled) {
      const locationIdNum = Number.parseInt(locationIdFromUrl, 10);
      const location = options.find((l) => l.location_id === locationIdNum);
      if (location) {
        setValue('location_id', locationIdFromUrl);
        setSelectedLocation(location);
        const locationName =
          location.location_name || location.business_name || `Location ${location.location_id}`;
        const hasATMs = location.installed_atms && location.installed_atms.length > 0;
        const label = hasATMs
          ? `${locationName} (${location.installed_atms.length} ATM${location.installed_atms.length > 1 ? 's' : ''})`
          : `${locationName} (No ATMs)`;
        setInputValue(label);
      }
    }
  }, [locationIdFromUrl, options, setValue, control, disabled]);

  // Sync inputValue and selectedLocation with form value
  useEffect(() => {
    if (watchedLocationId) {
      const locationIdNum = Number.parseInt(watchedLocationId, 10);
      if (selectedLocation?.location_id === locationIdNum) {
        return;
      }
      const location = options.find((l) => l.location_id === locationIdNum);
      if (location) {
        setSelectedLocation(location);
        const locationName =
          location.location_name || location.business_name || `Location ${location.location_id}`;
        const hasATMs = location.installed_atms && location.installed_atms.length > 0;
        const label = hasATMs
          ? `${locationName} (${location.installed_atms.length} ATM${location.installed_atms.length > 1 ? 's' : ''})`
          : `${locationName} (No ATMs)`;
        if (!open) {
          setInputValue(label);
        }
      } else if (!open) {
        setInputValue(`Location ${watchedLocationId}`);
      }
    } else {
      setSelectedLocation(null);
      if (!open) {
        setInputValue('');
      }
    }
  }, [watchedLocationId, options, selectedLocation, open]);

  // Update input when location is set from case
  useEffect(() => {
    if (watchedCaseId) {
      // Location will be set by the parent component when case is selected
      // Just clear the search query since it's disabled
      if (!watch('location_id')) {
        setInputValue('');
        setSelectedLocation(null);
      }
    }
  }, [watchedCaseId, watch('location_id')]);

  const getLocationSearchText = (location: Location) => {
    const addressParts = [
      location.street_address || location.address,
      location.city,
      location.state,
      location.zipcode,
      location.full_address,
    ].filter(Boolean);
    const address = addressParts.join(' ');
    return [
      location.location_id?.toString(),
      location.location_name,
      location.business_name,
      address,
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  };

  return (
    <Controller
      name="location_id"
      control={control}
      render={({ field }) => {
        return (
          <Autocomplete
            open={open}
            onOpen={() => {
              if (!disabled) setOpen(true);
            }}
            onClose={() => setOpen(false)}
            value={
              selectedLocation ||
              options.find((loc) => loc.location_id.toString() === field.value) ||
              null
            }
            onChange={(_, newValue) => {
              field.onChange(newValue ? newValue.location_id.toString() : '');
              setSelectedLocation(newValue);
              if (newValue) {
                const locationName =
                  newValue.location_name ||
                  newValue.business_name ||
                  `Location ${newValue.location_id}`;
                const hasATMs = newValue.installed_atms && newValue.installed_atms.length > 0;
                const label = hasATMs
                  ? `${locationName} (${newValue.installed_atms.length} ATM${newValue.installed_atms.length > 1 ? 's' : ''})`
                  : `${locationName} (No ATMs)`;
                setInputValue(label);
              } else {
                setInputValue('');
              }
            }}
            inputValue={inputValue}
            onInputChange={(_, newInputValue, reason) => {
              if (reason === 'input') {
                setInputValue(newInputValue);
              } else if (reason === 'reset' && selectedLocation) {
                const locationName =
                  selectedLocation.location_name ||
                  selectedLocation.business_name ||
                  `Location ${selectedLocation.location_id}`;
                const hasATMs =
                  selectedLocation.installed_atms && selectedLocation.installed_atms.length > 0;
                const label = hasATMs
                  ? `${locationName} (${selectedLocation.installed_atms.length} ATM${selectedLocation.installed_atms.length > 1 ? 's' : ''})`
                  : `${locationName} (No ATMs)`;
                setInputValue(label);
              }
            }}
            options={options}
            filterOptions={(optionList, state) => {
              const input = state.inputValue.trim().toLowerCase();
              if (!input) return optionList;
              return optionList.filter((option) => getLocationSearchText(option).includes(input));
            }}
            getOptionLabel={(option) => {
              if (typeof option === 'string') return option;
              const locationName =
                option.location_name || option.business_name || `Location ${option.location_id}`;
              const hasATMs = option.installed_atms && option.installed_atms.length > 0;
              if (hasATMs) {
                const atmCount = option.installed_atms.length;
                return `${locationName} (${atmCount} ATM${atmCount > 1 ? 's' : ''})`;
              }
              return `${locationName} (No ATMs)`;
            }}
            renderOption={(props, location) => {
              const hasATMs = location.installed_atms && location.installed_atms.length > 0;
              const locationName =
                location.location_name ||
                location.business_name ||
                `Location ${location.location_id}`;

              const addressParts = [
                location.street_address || location.address,
                location.city,
                location.state,
                location.zipcode,
              ].filter(Boolean);
              const address =
                addressParts.length > 0 ? addressParts.join(', ') : location.full_address || '';

              return (
                <li {...props} key={location.location_id}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                      <Typography variant="body2" sx={{ fontWeight: hasATMs ? 500 : 400 }}>
                        {locationName}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        ID: {location.location_id}
                      </Typography>
                    </Box>
                    {address && (
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
                        {address}
                      </Typography>
                    )}
                    {hasATMs ? (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.25 }}>
                        {location.installed_atms.map((atm) => (
                          <Chip
                            key={atm.atm_id}
                            label={`ATM ${atm.atm_id}`}
                            size="small"
                            color="success"
                            sx={{
                              height: 20,
                              fontSize: '0.65rem',
                              fontWeight: 600,
                            }}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Chip
                        label="No ATMs"
                        size="small"
                        color="default"
                        sx={{
                          height: 20,
                          fontSize: '0.65rem',
                          opacity: 0.7,
                          alignSelf: 'flex-start',
                          mt: 0.25,
                        }}
                      />
                    )}
                  </Box>
                </li>
              );
            }}
            loading={isLoading || isFetching}
            size="small"
            disabled={disabled}
            renderInput={(params) => (
              <TextField
                {...params}
                label={label}
                placeholder={disabled ? 'Location inherited from case' : 'Search locations...'}
                error={!!errors.location_id}
                helperText={
                  helperText ||
                  (errors.location_id?.message as string) ||
                  (watchedCaseId
                    ? 'Location is automatically set from the selected case'
                    : 'Required if no case is selected')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option.location_id === value.location_id}
            noOptionsText={debouncedSearchText ? 'No locations found' : 'Type to search locations'}
            ListboxProps={{
              onScroll: (event) => {
                const listboxNode = event.currentTarget;
                if (
                  listboxNode.scrollTop + listboxNode.clientHeight >=
                    listboxNode.scrollHeight - 50 &&
                  hasMore &&
                  !isFetching
                ) {
                  setCurrentPage((prev) => prev + 1);
                }
              },
            }}
          />
        );
      }}
    />
  );
};
